"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Address = exports.addressAttributes = void 0;
const IValidatable_1 = require("./IValidatable");
const Uuidable_1 = require("./Uuidable");
const Result_type_1 = require("../../types/Result.type");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const ConstructorUtils_1 = require("../../utils/ConstructorUtils");
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
// TODO: make this class more international. For now, it fits Canadian addresses only.
exports.addressAttributes = [
    ...Uuidable_1.uuidableAttributeNames,
    ...IValidatable_1.validatableAttributeNames,
    'fullAddress', // full address string, when coming from the survey and/or entered by humans
    'civicNumber',
    'civicNumberSuffix',
    'unitNumber',
    'streetName',
    'streetNameHomogenized',
    'combinedStreetUuid',
    'municipalityName',
    'municipalityCode',
    'postalMunicipalityName',
    'region',
    'country',
    'postalCode',
    'postalId',
    'combinedAddressUuid'
];
/**
 * Address objects are created from official data sources and are used
 * to match geolocations with a civic address.
 */
class Address extends Uuidable_1.Uuidable {
    constructor(params) {
        super(params._uuid);
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes(params, exports.addressAttributes);
        this._attributes = attributes;
        this._customAttributes = customAttributes;
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _isValid() {
        return this._attributes._isValid;
    }
    set _isValid(value) {
        this._attributes._isValid = value;
    }
    get fullAddress() {
        return this._attributes.fullAddress;
    }
    set fullAddress(value) {
        this._attributes.fullAddress = value;
    }
    get civicNumber() {
        return this._attributes.civicNumber;
    }
    set civicNumber(value) {
        this._attributes.civicNumber = value;
    }
    get civicNumberSuffix() {
        return this._attributes.civicNumberSuffix;
    }
    set civicNumberSuffix(value) {
        this._attributes.civicNumberSuffix = value;
    }
    get unitNumber() {
        return this._attributes.unitNumber;
    }
    set unitNumber(value) {
        this._attributes.unitNumber = value;
    }
    get streetName() {
        return this._attributes.streetName;
    }
    set streetName(value) {
        this._attributes.streetName = value;
    }
    get streetNameHomogenized() {
        return this._attributes.streetNameHomogenized;
    }
    set streetNameHomogenized(value) {
        this._attributes.streetNameHomogenized = value;
    }
    get combinedStreetUuid() {
        return this._attributes.combinedStreetUuid;
    }
    set combinedStreetUuid(value) {
        this._attributes.combinedStreetUuid = value;
    }
    get municipalityName() {
        return this._attributes.municipalityName;
    }
    set municipalityName(value) {
        this._attributes.municipalityName = value;
    }
    get municipalityCode() {
        return this._attributes.municipalityCode;
    }
    set municipalityCode(value) {
        this._attributes.municipalityCode = value;
    }
    get postalMunicipalityName() {
        return this._attributes.postalMunicipalityName;
    }
    set postalMunicipalityName(value) {
        this._attributes.postalMunicipalityName = value;
    }
    get region() {
        return this._attributes.region;
    }
    set region(value) {
        this._attributes.region = value;
    }
    get country() {
        return this._attributes.country;
    }
    set country(value) {
        this._attributes.country = value;
    }
    get postalCode() {
        return this._attributes.postalCode;
    }
    set postalCode(value) {
        this._attributes.postalCode = value;
    }
    get postalId() {
        return this._attributes.postalId;
    }
    set postalId(value) {
        this._attributes.postalId = value;
    }
    get combinedAddressUuid() {
        return this._attributes.combinedAddressUuid;
    }
    set combinedAddressUuid(value) {
        this._attributes.combinedAddressUuid = value;
    }
    /**
     * Creates an Address object from sanitized parameters
     * @param {ExtendedAddressAttributes | SerializedExtendedAddressAttributes} params - Sanitized address parameters
     * @param {SurveyObjectsRegistry} _surveyObjectsRegistry - unused since Address is not composed of other objects, but unserializer will send the optional param.
     * @returns {Address} New Address instance
     */
    static unserialize(params, _surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new Address(flattenedParams);
    }
    /**
     * Factory that validates input from an interview and makes
     * sure types and required fields are valid before returning a new object
     * Custom attributes must be validated in each survey project
     * @param dirtyParams
     * @returns Address | Error[]
     */
    static create(dirtyParams) {
        const errors = Address.validateParams(dirtyParams);
        const address = errors.length === 0 ? new Address(dirtyParams) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(address);
    }
    validate() {
        // TODO: implement:
        this._attributes._isValid = true;
        return true;
    }
    isValid() {
        return this._isValid;
    }
    /**
     * Validates attributes types for Address.
     * @param dirtyParams The parameters to validate.
     * @param displayName The name of the object to validate, for error display
     * @returns Error[] TODO: specialize this error class
     */
    static validateParams(dirtyParams, displayName = 'Address') {
        const errors = [];
        // Validate params object:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        // Validate _uuid
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams));
        // Validate _isValid
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_isValid', dirtyParams._isValid, displayName));
        // Validate attributes
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('fullAddress', dirtyParams.fullAddress, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('civicNumber', dirtyParams.civicNumber, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('civicNumberSuffix', dirtyParams.civicNumberSuffix, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('unitNumber', dirtyParams.unitNumber, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isNonEmptyString('streetName', dirtyParams.streetName, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('streetNameHomogenized', dirtyParams.streetNameHomogenized, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('combinedStreetUuid', dirtyParams.combinedStreetUuid, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isNonEmptyString('municipalityName', dirtyParams.municipalityName, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('municipalityCode', dirtyParams.municipalityCode, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('postalMunicipalityName', dirtyParams.postalMunicipalityName, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isNonEmptyString('region', dirtyParams.region, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isNonEmptyString('country', dirtyParams.country, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('postalCode', dirtyParams.postalCode, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('postalId', dirtyParams.postalId, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('combinedAddressUuid', dirtyParams.combinedAddressUuid, displayName));
        return errors;
    }
}
exports.Address = Address;
Address._confidentialAttributes = [];
//# sourceMappingURL=Address.js.map